import * as moment from 'moment';
import * as _ from "lodash";
import { useMemo, useState } from 'react';
import { IDonorRegistration, IDonorStatus } from '@nkr/models/index';
import { BodyScrollEvent, CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { VoucherEducationChoice } from "@nkr/enums/voucher-education-choice";
import { AgGridReact } from 'ag-grid-react';
import { ButtonToolbar, FormControl, Glyphicon, Label, OverlayTrigger, Popover } from 'react-bootstrap';
import { GridWrapper } from '../components/all/styles';
import { Utility } from '@nkr/services/utility';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { UseSearchReturn } from 'ClientApp/utils/rest-api';
import { IDashListSummary, IDashSearch } from '@nkr/models/dash';
import { OrganType } from '@nkr/enums/organ-type';
import registration from '@nkr/views/qa/registration';
import { RegistrationStep } from '@nkr/enums/registration-step';

const popoverHoverFocus = (id: string, msg: string) => (
    <Popover id={id}>
        {msg}
    </Popover>
);

interface IProps {
    search: UseSearchReturn<IDashSearch, IDashListSummary>,
}

export const SearchIntendedRecipientGrid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.setColumnDefs(getColumnChanges());
        e.api.sizeColumnsToFit();
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const getColumnChanges = () => {
        let cols: ColDef[] = [
            {
                colId: '1',
                field: 'rank',
                headerName: 'R#',
                headerComponentFramework: (params: IHeaderParams) => <div>R#</div>,
                sortable: false,
                width: 30,
                pinned: 'left',
                pinnedRowCellRendererFramework: () => <div></div>,
                valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
            },
            {
                field: 'source',
                headerName: 'Source',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Source</SortableHeaderComponent>,
                sortable: true,
                width: 80,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDashListSummary;

                    let style = 'default';

                    switch (data.source) {
                        case 'NKR': style = 'info'; break;
                        case 'DASH': style = 'default'; break;
                        case 'MiSite': style = 'success'; break;
                        case 'UDASH': style = 'warning'; break;
                    }

                    return <Label bsStyle={style}>{params.value}</Label>;
                }
            },
            {
                field: 'organType',
                headerName: 'O',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><span title='Organ'>Org</span></SortableHeaderComponent>,
                sortable: true,
                width: 30,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDashListSummary;
                    return data.organType == OrganType.Kidney ? <Label bsStyle='info'>K</Label> : <Label bsStyle='warning'>L</Label>
                }
            },
            {
                field: 'intendedRecipientFirstName',
                headerName: 'intendedRecipientFirstName',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Patient <br />First Name</SortableHeaderComponent>,
                width: 130,
                sortable: true,
            },
            {
                field: 'intendedRecipientLastName',
                headerName: 'intendedRecipientLastName',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Patient <br />Last Name</SortableHeaderComponent>,
                width: 130,
                sortable: true,
            },
            {
                field: 'intendedRecipientDob',
                headerName: 'intendedRecipientDob',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Patient <br />DOB</SortableHeaderComponent>,
                hide: window.innerWidth < 800,
                width: 130,
                sortable: true,
                comparator: Utility.dateComparator
            },
            {
                field: 'intendedDonationType',
                headerName: 'intendedDonationType',
                sortable: true,
                width: 80,
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Type</SortableHeaderComponent>,
                hide: window.innerWidth < 800,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDashListSummary;

                    var style = 'default';
                    switch (data.intendedDonationType) {
                        case 'SV': style = 'success'; break;
                        case 'FV': style = 'success'; break;
                        case 'NDD': style = 'info'; break;
                        case 'DD': style = 'default'; break;
                        default: style = 'default'; break;
                    }

                    return <Label bsStyle={style}>{data.intendedDonationType}</Label>;
                }
            },
            {
                field: 'intendedRecipientRelationship',
                headerName: 'intendedRecipientRelationship',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Relationship<br />to<br />Patient</SortableHeaderComponent>,
                hide: window.innerWidth < 1600,
                width: 130,
                sortable: true,
            },
            {
                field: 'registrationStep',
                headerName: 'registrationStep',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
                sortable: true,
                sortingOrder: ['desc', 'asc', null],
                width: 100,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IDashListSummary;
                    if (data.registrationStep < 1000) { return 0; }
                    if (data.registrationStep == 3001) { return 2; }
                    if (data.registrationStep == 3000) { return 1; }
                    if (data.registrationStep >= 2000) { return 3; }
                    if (data.registrationStep >= 1000) { return 4; }
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    var msg, className, color = "black";
                    if (params.data.registrationStep == 3001) { msg = "Duplicate", className = 'glyphicon glyphicon-duplicate'; }
                    else if (params.data.registrationStep == 3000) { msg = "Transferred", className = 'glyphicon glyphicon-transfer'; }
                    else if (params.data.registrationStep >= 2000) { msg = "Ruled Out", className = 'glyphicon glyphicon-remove', color = "#CC0000"; }
                    else if (params.data.registrationStep < 1000 || params.data.hasUsedAlcohol === null) { msg = "Incomplete", className = 'glyphicon glyphicon-adjust', color = "orange"; }
                    else if (params.data.registrationStep >= 1000) { msg = "Complete", className = 'glyphicon glyphicon-check', color = "#00AA00"; }

                    return (
                        <OverlayTrigger
                            trigger={['hover', 'focus']}
                            placement='bottom'
                            container={() => document.body}
                            overlay={popoverHoverFocus(`popover-step-${params.data.id}`, msg)}>
                            <span className={className} style={{ color: color }}></span>
                        </OverlayTrigger>
                    );
                }
            },

            {
                field: 'firstName',
                headerName: 'firstName',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />First Name</SortableHeaderComponent>,
                sortable: true,
                width: 100,
            },

            {
                field: 'lastName',
                headerName: 'lastName',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Last Name</SortableHeaderComponent>,
                sortable: true,
                width: 100,
            },
            {
                field: 'registrationStatus',
                headerName: 'registrationStatus',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registration<br />Status</SortableHeaderComponent>,
                hide: window.innerWidth < 1600,
                sortingOrder: ['desc', 'asc', null],
                sortable: true,
                width: 150,
            },
            {
                field: 'location',
                headerName: 'location',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Location</SortableHeaderComponent>,
                hide: window.innerWidth < 800,
                sortable: true,
                width: 100,
            },
            {
                field: 'registrationDate',
                headerName: 'registrationDate',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date<br />Registered</SortableHeaderComponent>,
                hide: window.innerWidth < 1600,
                sortable: true,
                width: 150,
                comparator: Utility.dateComparator
            },
            {
                field: 'intakeCompleteDate',
                headerName: 'intakeCompleteDate',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date<br />Referred</SortableHeaderComponent>,
                sortable: true,
                hide: window.innerWidth < 1600,
                width: 150,
                comparator: Utility.dateComparator
            },
            {
                field: 'registrationCenterAlias',
                headerName: 'registrationCenterAlias',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Referred<br />Center</SortableHeaderComponent>,
                hide: window.innerWidth < 1600,
                width: 140,
                sortable: true,
                cellRendererFramework: (params: ICellRendererParams) => {
                    const data          = params.data as IDashListSummary;
                    const centerAlias   = String(data.registrationCenterAlias)?.trim();
                    return centerAlias === '' ? '--' : <Label bsStyle="warning">{centerAlias}</Label>
                }
            },
            {
                field: 'donationDate',
                headerName: 'donationDate',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date<br />Donated</SortableHeaderComponent>,
                sortable: true,
                hide: window.innerWidth < 1600,
                width: 130,
                comparator: Utility.dateComparator
            },
            {
                field: 'centerWorkflowDescription',
                headerName: 'centerWorkflowDescription',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Workflow<br />Status</SortableHeaderComponent>,
                hide: window.innerWidth < 1600,
                sortable: true,
                width: 130
            },
            {
                field: 'networkStatusDescription',
                headerName: 'networkStatusDescription',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Network<br />Status</SortableHeaderComponent>,
                hide: window.innerWidth < 800,
                sortable: true,
                width: 130
            },
        ]
        return cols;
    };

    const columnDefs = useMemo(() => getColumnChanges(), []);
    const rowData = useMemo(() => props.search.data, [props.search.data]);

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ height: '70vh' }}>
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={60}
                rowHeight={34}
                enableCellChangeFlash={true}
                suppressCellSelection={true}
                suppressMultiSort={true}
                rowStyle={{ overflow: 'visible' }}
                noRowsOverlayComponentFramework={() => <div>...</div>}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onRowClicked={(e: CellClickedEvent) => {
                    const data = e.data as IDashListSummary;
                    props.search.selectRow(data.id, data);
                }}
                onBodyScroll={onBodyScroll}
                onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
            />
        </GridWrapper>
    )
}